import { useCallback } from 'react';
import { Theme } from '@app/theme';
import { IconChip, InformationBox } from '@app/core';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import {
  BillingMethod,
  Feature,
  QuoteInformation,
  useCapabilitiesFirstOffer,
} from '@app/api';
import { CheckboxBlockField, CountryKeySelect } from 'components';
import { SetupCheckboxItemContainer } from '../../components/SetupCheckboxItemContainer';

const useStyles = createUseStyles((theme: Theme) => ({
  countryKeySelectContainer: {
    display: 'flex',
    marginTop: theme.marginBase * 3,
    flexDirection: 'column',
    gap: theme.marginBase,
  },
}));

interface Props {
  showBillingCountry?: boolean;
}

export const BillingMethodSection = ({ showBillingCountry = true }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { setFieldValue } = useFormikContext<Partial<QuoteInformation>>();

  const onCountryKeyChange = useCallback(
    (countryKey: any) => {
      setFieldValue('billingCountry', countryKey?.value);
    },
    [setFieldValue],
  );

  const invoiceFeeOffer = useCapabilitiesFirstOffer(Feature.SEND_INVOICE);
  return (
    <>
      <SetupCheckboxItemContainer>
        <CheckboxBlockField
          type="radio"
          name="billingMethod"
          noErrorMsg
          variant="green"
          label={t('studySetup.generalSetup.billingMethod.CREDIT_CARD')}
          icon="ri-bank-card-line"
          value={BillingMethod.CREDIT_CARD}
        />
        <CheckboxBlockField
          type="radio"
          name="billingMethod"
          noErrorMsg
          variant="green"
          label={t('studySetup.generalSetup.billingMethod.SEND_INVOICE')}
          icon="ri-file-list-3-line"
          value={BillingMethod.SEND_INVOICE}
          feature={Feature.SEND_INVOICE}
          featureChildren={
            <IconChip
              color={invoiceFeeOffer?.color}
              label={invoiceFeeOffer?.shortName}
            />
          }
        />
      </SetupCheckboxItemContainer>
      {showBillingCountry && (
        <div className={classes.countryKeySelectContainer}>
          <CountryKeySelect
            noMargin
            menuPortalTarget={document.body}
            name="countryKey"
            placeholder={t('studySetup.generalSetup.cost.billingCountry')}
            onChange={onCountryKeyChange}
            opaquePlaceholder={true}
          />
          <InformationBox variant="info">
            {t('studySetup.generalSetup.cost.billingCountryInfo')}
          </InformationBox>
        </div>
      )}
    </>
  );
};
